/**
 * PriorManagerPanel — Vor-Verwalter-Tilgung & Verwalterbeginn (P2).
 *
 * Zeigt den Verwalterbeginn (aus `property.management_start`) und erlaubt es,
 * offene Beträge aus der Zeit VOR dem Verwalterbeginn pauschal als getilgt zu
 * markieren. Erzeugt synthetische Ausgleichs-Ist-Buchungen für jedes aktive MV
 * des Objekts.
 */
import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Warning, CheckCircle, ClockCounterClockwise } from "@phosphor-icons/react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

export default function PriorManagerPanel({ property, onChange }) {
  const [saving, setSaving] = useState(false);
  const [msDate, setMsDate] = useState(property?.management_start || "");
  const [showEdit, setShowEdit] = useState(!property?.management_start);

  const saveManagementStart = async () => {
    if (!msDate) { toast.error("Datum wird benötigt"); return; }
    setSaving(true);
    try {
      await api.patch(`/properties/${property.id}`, { management_start: msDate });
      toast.success("Verwalterbeginn gespeichert");
      setShowEdit(false);
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const settle = async () => {
    if (!await window._domivusConfirm(
      `Alle offenen Beträge VOR dem ${fmtDate(property.management_start)} pauschal als „durch Vor-Verwalter getilgt" markieren?\n\nEs werden für jedes aktive Mietverhältnis dieses Objekts synthetische Ausgleichs-Ist-Buchungen erzeugt. Später importierte Kontoauszüge aus der Vor-Verwalter-Zeit ersetzen diese automatisch.`
    )) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/properties/${property.id}/prior-manager/settle`);
      toast.success(`Als getilgt markiert · ${data.postings_created} Ausgleichsbuchungen erzeugt`);
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const unsettle = async () => {
    if (!await window._domivusConfirm("Vor-Verwalter-Tilgung wirklich zurücksetzen? Alle Ausgleichsbuchungen werden gelöscht.")) return;
    setSaving(true);
    try {
      const { data } = await api.delete(`/properties/${property.id}/prior-manager/settle`);
      toast.success(`Rückgesetzt · ${data.postings_deleted} Ausgleichsbuchungen gelöscht`);
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="prior-manager-panel">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
        <ClockCounterClockwise size={16} className="text-slate-500" />
        <div className="text-sm font-semibold text-slate-800">Vor-Verwalter-Zeit</div>
      </div>
      <div className="p-4 space-y-3">
        {/* Verwalterbeginn */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Verwalterbeginn</div>
          {showEdit ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Input type="date" value={msDate} onChange={(e) => setMsDate(e.target.value)}
                     className="h-8 w-40" data-testid="management-start-input" />
              <Button size="sm" onClick={saveManagementStart} disabled={saving} data-testid="management-start-save">
                Speichern
              </Button>
              {property?.management_start && (
                <Button size="sm" variant="ghost" onClick={() => setShowEdit(false)}>Abbrechen</Button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-slate-800">
              <span className="font-mono">{fmtDate(property.management_start)}</span>
              <Button size="sm" variant="ghost" onClick={() => setShowEdit(true)}
                      className="h-7 text-xs" data-testid="management-start-edit">
                ändern
              </Button>
            </div>
          )}
        </div>

        {/* Tilgung */}
        {property?.management_start && (
          <div className="border-t border-slate-100 pt-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Pauschal-Tilgung offener Beträge vor Verwalterbeginn
            </div>
            {property?.prior_manager_settled ? (
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2">
                <CheckCircle size={18} weight="fill" className="text-emerald-700 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-emerald-900 font-medium">
                    Als „durch Vor-Verwalter getilgt" markiert
                  </div>
                  <div className="text-xs text-emerald-700 mt-0.5">
                    Später importierte Kontoauszüge aus der Vor-Verwalter-Zeit ersetzen die
                    synthetischen Buchungen automatisch.
                  </div>
                  <Button size="sm" variant="outline" onClick={unsettle} disabled={saving}
                          className="mt-2 h-7 text-xs border-emerald-300 text-emerald-800"
                          data-testid="prior-manager-unsettle">
                    Rückgängig
                  </Button>
                </div>
              </div>
            ) : (
              <div className="rounded-md bg-amber-50/50 border border-amber-200 p-3 flex items-start gap-2">
                <Warning size={18} className="text-amber-700 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-slate-900">
                    Offene Beträge aus der Zeit vor <b>{fmtDate(property.management_start)}</b> können
                    pauschal als getilgt gebucht werden.
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    Beim späteren Import echter Kontoauszüge (vor {fmtDate(property.management_start)})
                    werden die pauschalen Buchungen ersetzt — nichts wird doppelt gebucht.
                  </div>
                  <Button size="sm" onClick={settle} disabled={saving}
                          className="mt-2 h-7 text-xs bg-amber-700 hover:bg-amber-800 text-white"
                          data-testid="prior-manager-settle">
                    Als pauschal getilgt markieren
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
